import { useWorkersQuery } from '../queries/users'
import sc from 'string-comparison'
import { getUserLabel, getWorkerAndOrganizationInternalRef } from '../utils/users'
import { sortBy, upperFirst } from 'lodash'
import { FetchWorkersParams, User } from '../api/users'
import useStore from '../store'
import { QueryOptions } from '../queries'
import { Organization } from '../api/organizations'

interface WorkersSearchResult {
  value: string
  label: string
  rating: number
}

/**
 * Calculates a relevance rating for a worker based on search query
 * Uses multiple matching strategies with weighted scoring:
 * - Exact prefix matches get highest priority
 * - Levenshtein similarity for fuzzy matching
 * - Different weights for first name, last name, and full label
 */
const calculateRating = (
  worker: User,
  search?: string,
  currentOrganization?: Organization,
): number => {
  if (!search || search.trim().length === 0) return 0

  const searchTerm = search.trim().toLowerCase()
  const firstName = worker.firstName.toLowerCase()
  const lastName = worker.lastName.toLowerCase()
  const fullLabel = getUserLabel(worker).toLowerCase()
  const fullName = `${firstName} ${lastName}`.toLowerCase()
  const internalRef = getWorkerAndOrganizationInternalRef(worker, currentOrganization)
  // Exact prefix matches get highest scores
  const firstNameExactPrefix = firstName.startsWith(searchTerm) ? 1.0 : 0
  const lastNameExactPrefix = lastName.startsWith(searchTerm) ? 1.0 : 0

  // Fuzzy matching with Levenshtein for non-exact matches
  const firstNameFuzzy =
    firstNameExactPrefix === 0 ? sc.levenshtein.similarity(searchTerm, firstName) : 0

  const lastNameFuzzy =
    lastNameExactPrefix === 0 ? sc.levenshtein.similarity(searchTerm, lastName) : 0

  const fullNameFuzzy = sc.levenshtein.similarity(searchTerm, fullName)

  // Full label similarity for broader context
  const fullLabelFuzzy = sc.levenshtein.similarity(searchTerm, fullLabel)

  // Internal ref similarity for internal ref matches
  const internalRefFuzzy = internalRef ? sc.levenshtein.similarity(searchTerm, internalRef) : 0

  // Weighted scoring system
  const weights = {
    internalRef: 3.0, // Highest priority for internal ref matches
    firstNameExact: 3.0, // Highest priority for exact first name matches
    lastNameExact: 3.0, // High priority for exact last name matches
    firstNameFuzzy: 1.5, // Medium priority for fuzzy first name
    lastNameFuzzy: 1.5, // Medium priority for fuzzy last name
    fullNameFuzzy: 2.8, // Medium priority for fuzzy full name
    fullLabel: 0.8, // Lower priority for full label matches
  }

  // Calculate weighted score
  const score =
    firstNameExactPrefix * weights.firstNameExact +
    lastNameExactPrefix * weights.lastNameExact +
    firstNameFuzzy * weights.firstNameFuzzy +
    lastNameFuzzy * weights.lastNameFuzzy +
    fullNameFuzzy * weights.fullNameFuzzy +
    fullLabelFuzzy * weights.fullLabel +
    internalRefFuzzy * weights.internalRef

  // Normalize score to 0-1 range and apply bonus for shorter search terms
  const normalizedScore = Math.min(
    score / Object.values(weights).reduce((sum, w) => sum + w, 0),
    1.0,
  )

  // Bonus for shorter search terms (more specific matches)
  const lengthBonus = searchTerm.length <= 2 ? 0.1 : 0

  return Math.min(normalizedScore + lengthBonus, 1.0)
}

const useWorkersSearch = (
  search: string,
  queryOptions?: QueryOptions<any>,
  params?: FetchWorkersParams,
): WorkersSearchResult[] => {
  const workersQuery = useWorkersQuery(params || {}, {
    staleTime: 60 * 1000 * 3,
    ...(queryOptions || {}),
  })
  const currentOrganization = useStore(state => state.session?.currentOrganization)

  const fetchedWorkers = workersQuery.data || []

  const workersWithMatchRating = sortBy(
    fetchedWorkers.map(worker => ({
      value: worker._id,
      label: upperFirst(getUserLabel(worker)),
      rating: calculateRating(worker, search, currentOrganization),
    })),
    search ? 'rating' : 'label',
  )

  // If no search, return all workers sorted alphabetically
  if (!search) return workersWithMatchRating

  // For search results, sort by rating (highest first) and filter by relevance
  workersWithMatchRating.reverse()

  // Get the best rating to establish a relevance threshold
  const bestRating = workersWithMatchRating[0]?.rating || 0

  // Apply intelligent filtering based on search length and relevance
  const limited = workersWithMatchRating
    .slice(0, 8) // Show more results for better UX
    .filter(match => {
      // For very short searches (1-2 chars), be more permissive
      if (search.length <= 2) {
        return match.rating > 0.1 // Accept any meaningful match
      }

      // For longer searches, require higher relevance
      const relevanceThreshold = bestRating * 0.3 // 30% of best match
      return match.rating >= relevanceThreshold
    })

  return limited
}

export default useWorkersSearch
